import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigationUtils } from '../utils/navigationUtils';
import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  Snackbar,
  Divider,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Paper,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from '@mui/material';

import {
  CalendarToday as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  OpenInNew as OpenInNewIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  RateReview as RateReviewIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Work as WorkIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useAuth } from '../services/AuthService';
import JobService from '../services/JobService';
import CompanyProfileService from '../services/CompanyProfileService';
import ApplicationService from '../services/ApplicationService';
import JobSeekerProfileService from '../services/JobSeekerProfileService';
import FeedbackDialog from '../components/FeedbackDialog';
import { usePostHog } from '@posthog/react';

const JOBS_PER_PAGE = 20;

const cleanPhoneForWhatsApp = (phone) => {
  if (!phone) return null;
  const cleaned = phone.replace(/[^\d+]/g, '').replace(/^\+/, '');
  return cleaned.length >= 7 ? cleaned : null;
};

const CompanyJobsManagement = () => {
  const { user, isAuthenticated, loginWithRedirect } = useAuth();
  const { navigateBackToDashboard } = useNavigationUtils();
  const posthog = usePostHog();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [updatingJobs, setUpdatingJobs] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [companyProfile, setCompanyProfile] = useState(null);
  const [jobApplicationsByJobId, setJobApplicationsByJobId] = useState({});
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [loadingApplicationsByJobId, setLoadingApplicationsByJobId] = useState(new Set());
  const [shortlistedByJobId, setShortlistedByJobId] = useState({});
  const [shortlistFilterByJobId, setShortlistFilterByJobId] = useState({});
  const [togglingShortlist, setTogglingShortlist] = useState(new Set());
  const [activeTab, setActiveTab] = useState(0); // 0=All, 1=Published, 2=Unpublished
  const [searchQuery, setSearchQuery] = useState('');
  const [feedbackTarget, setFeedbackTarget] = useState(null); // { jobSeekerProfileId, name, jobId, applicationId }

  const fetchCompanyJobs = useCallback(async (cursor = null) => {
    try {
      if (cursor) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const companyProfileService = new CompanyProfileService(user);
      const profile = await companyProfileService.getRecordsByUserId(user.sub);

      if (!profile) {
        setSnackbar({
          open: true,
          message: 'Company profile not found. Please complete your company profile first.',
          severity: 'warning'
        });
        setJobs([]);
        setCompanyProfile(null);
        setJobApplicationsByJobId({});
        setExpandedJobId(null);
        setNextCursor(null);
        setTotalCount(null);
        return;
      }

      setCompanyProfile(profile);

      const jobService = new JobService(user);
      const result = await jobService.getJobsByCompany(profile.id, { limit: JOBS_PER_PAGE, cursor });

      if (cursor) {
        setJobs(prev => [...prev, ...result.items]);
      } else {
        setJobs(result.items);
      }
      setNextCursor(result.nextCursor);
      if (result.totalCount != null) setTotalCount(result.totalCount);
    } catch (error) {
      console.error('Error fetching company jobs:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch your jobs. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchCompanyJobs();
    }
  }, [isAuthenticated, user, fetchCompanyJobs]);

  const dateTimeFormatter = useMemo(() => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const toggleJobPublication = async (jobId, currentStatus) => {
    try {
      setUpdatingJobs(prev => new Set([...prev, jobId]));
      const jobService = new JobService(user);
      await jobService.toggleJobPublication(jobId, !currentStatus);
      posthog?.capture('job_publication_toggled', { job_id: jobId, new_status: !currentStatus ? 'published' : 'unpublished' });
      
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === jobId 
            ? { ...job, isPublished: !currentStatus }
            : job
        )
      );

      setSnackbar({
        open: true,
        message: `Job ${!currentStatus ? 'published' : 'unpublished'} successfully.`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error toggling job publication:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update job status. Please try again.',
        severity: 'error'
      });
    } finally {
      setUpdatingJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return dateTimeFormatter.format(date);
  }, [dateTimeFormatter]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleBack = () => {
    navigateBackToDashboard();
  };

  const handleLoadMore = () => {
    if (nextCursor && !loadingMore && companyProfile) {
      fetchCompanyJobs(nextCursor);
    }
  };

  const fetchApplicationsForJob = useCallback(async (jobId) => {
    if (jobApplicationsByJobId[jobId]) return;
    if (loadingApplicationsByJobId.has(jobId)) return;

    setLoadingApplicationsByJobId(prev => new Set([...prev, jobId]));

    try {
      const applicationService = new ApplicationService(user);
      const jobSeekerService = new JobSeekerProfileService(user);

      const applications = await applicationService.getApplicationsByJobId(jobId);
      const applicationsWithDetails = await Promise.all(
        applications.map(async (application) => {
          try {
            const jobSeekerProfile = await jobSeekerService.getRecordById(application.jobSeekerProfileId);
            return { ...application, jobSeekerProfile, basicProfile: jobSeekerProfile?.basicProfile || null };
          } catch (error) {
            console.error(`Error fetching job seeker profile for application ${application.id}:`, error);
            return application;
          }
        })
      );

      // Also fetch the shortlist for this job
      try {
        const jobService = new JobService(user);
        const shortlisted = await jobService.getShortlistedApplicants(jobId);
        setShortlistedByJobId(prev => ({ ...prev, [jobId]: shortlisted }));
      } catch {
        setShortlistedByJobId(prev => ({ ...prev, [jobId]: [] }));
      }

      setJobApplicationsByJobId(prev => ({
        ...prev,
        [jobId]: applicationsWithDetails
      }));
    } catch (error) {
      console.error(`Error fetching applications for job ${jobId}:`, error);
      setJobApplicationsByJobId(prev => ({
        ...prev,
        [jobId]: []
      }));
    } finally {
      setLoadingApplicationsByJobId(prev => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  }, [jobApplicationsByJobId, loadingApplicationsByJobId, user]);

  const computeApplicationStats = useCallback((applications) => {
    const stats = { total: 0, pending: 0, reviewed: 0, accepted: 0, rejected: 0 };
    stats.total = applications.length;
    for (const application of applications) {
      if (application.status === 'pending') stats.pending += 1;
      else if (application.status === 'reviewed') stats.reviewed += 1;
      else if (application.status === 'accepted') stats.accepted += 1;
      else if (application.status === 'rejected') stats.rejected += 1;
    }
    return stats;
  }, []);

  const handleToggleShortlist = useCallback(async (jobId, jobSeekerProfileId) => {
    const key = `${jobId}:${jobSeekerProfileId}`;
    if (togglingShortlist.has(key)) return;
    setTogglingShortlist(prev => new Set([...prev, key]));

    try {
      const jobService = new JobService(user);
      const currentList = shortlistedByJobId[jobId] || [];
      const isCurrentlyShortlisted = currentList.includes(jobSeekerProfileId);

      let updatedList;
      if (isCurrentlyShortlisted) {
        updatedList = await jobService.removeFromShortlist(jobId, jobSeekerProfileId);
      } else {
        updatedList = await jobService.addToShortlist(jobId, jobSeekerProfileId);
      }
      posthog?.capture('applicant_shortlist_toggled', { job_id: jobId, applicant_id: jobSeekerProfileId, action: isCurrentlyShortlisted ? 'removed' : 'added' });
      setShortlistedByJobId(prev => ({ ...prev, [jobId]: updatedList }));
    } catch (error) {
      console.error('Error toggling shortlist:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update shortlist. Please try again.',
        severity: 'error'
      });
    } finally {
      setTogglingShortlist(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [user, shortlistedByJobId, togglingShortlist]);

  const handleToggleShortlistFilter = useCallback((jobId) => {
    setShortlistFilterByJobId(prev => ({ ...prev, [jobId]: !prev[jobId] }));
  }, []);

  // Computed: summary stats
  const jobStats = useMemo(() => {
    const published = jobs.filter(j => j.isPublished).length;
    return { total: jobs.length, published, unpublished: jobs.length - published };
  }, [jobs]);

  // Computed: filtered jobs by tab + search
  const filteredJobs = useMemo(() => {
    let result = jobs;
    // Tab filter
    if (activeTab === 1) result = result.filter(j => j.isPublished);
    else if (activeTab === 2) result = result.filter(j => !j.isPublished);
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(j =>
        (j.referenceCode || '').toLowerCase().includes(q) ||
        (j.enrichedData?.job_category || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [jobs, activeTab, searchQuery]);

  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
          <Box 
            sx={{ 
              bgcolor: 'background.paper', 
              p: 4, 
              borderRadius: 2, 
              boxShadow: 2,
              textAlign: 'center'
            }}
          >
            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Authentication Required</AlertTitle>
              You need to be logged in to view and manage your job postings. Join thousands of employers finding great talent!
            </Alert>
            
            <Stack spacing={2} sx={{ maxWidth: 400, mx: 'auto' }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={loginWithRedirect}
                size="large"
                fullWidth
              >
                Log In to Your Account
              </Button>
              
              <Typography variant="body2" color="text.secondary">
                Don't have an account?
              </Typography>
              
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={loginWithRedirect}
                size="large"
                fullWidth
              >
                Create New Account
              </Button>
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                ✓ Free to join • ✓ Easy setup • ✓ Start posting in minutes
              </Typography>
            </Stack>
          </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress size={60} color="primary" />
          </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            <Button
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              variant="text"
              color="inherit"
              sx={{
                alignSelf: 'flex-start',
                justifyContent: 'flex-start',
                width: 'fit-content'
              }}
            >
              Back to dashboard
            </Button>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              fontWeight="bold"
              sx={{ width: '100%', textAlign: 'center' }}
            >
              My Posted Jobs
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ width: '100%', textAlign: 'center' }}>
              {companyProfile?.companyName
                ? `Manage job postings for ${companyProfile.companyName}`
                : 'Manage your job postings and control their visibility'}
            </Typography>
          </Box>

          {/* Summary Stats */}
          {jobs.length > 0 && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {[
                { label: 'Total Jobs', value: totalCount ?? jobStats.total, icon: <WorkIcon />, color: 'primary.main' },
                { label: 'Published', value: jobStats.published, icon: <VisibilityIcon />, color: 'success.main' },
                { label: 'Unpublished', value: jobStats.unpublished, icon: <VisibilityOffIcon />, color: 'warning.main' },
              ].map(({ label, value, icon, color }) => (
                <Paper
                  key={label}
                  variant="outlined"
                  sx={{ flex: '1 1 140px', p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}
                >
                  <Box sx={{ color }}>{icon}</Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" lineHeight={1}>{value}</Typography>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}

          {/* Tabs + Search */}
          {jobs.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
              <Tabs
                value={activeTab}
                onChange={(_e, v) => setActiveTab(v)}
                sx={{ minHeight: 36 }}
              >
                <Tab label={`All (${jobStats.total})`} sx={{ minHeight: 36, textTransform: 'none' }} />
                <Tab label={`Published (${jobStats.published})`} sx={{ minHeight: 36, textTransform: 'none' }} />
                <Tab label={`Unpublished (${jobStats.unpublished})`} sx={{ minHeight: 36, textTransform: 'none' }} />
              </Tabs>
              <TextField
                size="small"
                placeholder="Search by title, reference, or category…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ ml: { sm: 'auto' }, maxWidth: 320 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
          )}

        {jobs.length === 0 ? (
          <Alert severity="info">
            <AlertTitle>No Jobs Posted</AlertTitle>
            You haven't posted any jobs yet. Start by creating your first job posting!
          </Alert>
        ) : filteredJobs.length === 0 ? (
          <Alert severity="info">
            No jobs match your current filters.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredJobs.length} of {totalCount ?? jobs.length} jobs — sorted by newest first
            </Typography>

            {filteredJobs.map((job) => {
              const isExpanded = expandedJobId === job.id;
              const applications = jobApplicationsByJobId[job.id];
              const isLoadingApplications = loadingApplicationsByJobId.has(job.id);
              const stats = applications ? computeApplicationStats(applications) : null;

              return (
                <Accordion
                  key={job.id}
                  expanded={isExpanded}
                  onChange={async (_event, nextExpanded) => {
                    if (nextExpanded) {
                      setExpandedJobId(job.id);
                      await fetchApplicationsForJob(job.id);
                    } else {
                      setExpandedJobId(null);
                    }
                  }}
                  disableGutters
                  variant="outlined"
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ width: '100%' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          justifyContent: 'space-between',
                          gap: 2,
                          flexWrap: 'wrap'
                        }}
                      >
                        <Box sx={{ minWidth: 240, flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            component="h3"
                            fontWeight="medium"
                            color={job.isPublished ? 'text.primary' : 'text.disabled'}
                          >
                            {job.referenceCode || `Job ID: ${job.id}`}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5, alignItems: 'center' }}>
                            {job.referenceCode && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {job.referenceCode}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                Posted {formatDate(job.createdAt)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <Chip
                            label={job.isPublished ? 'Published' : 'Unpublished'}
                            color={job.isPublished ? 'success' : 'warning'}
                            variant="filled"
                            size="small"
                          />
                          <Box sx={{ minWidth: 160 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={Boolean(job.isPublished)}
                                  disabled={updatingJobs.has(job.id)}
                                  onChange={() => toggleJobPublication(job.id, job.isPublished)}
                                  inputProps={{ 'aria-label': 'Toggle job publication' }}
                                />
                              }
                              label={updatingJobs.has(job.id) ? 'Updating…' : job.isPublished ? 'Live' : 'Hidden'}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PeopleIcon fontSize="small" color="primary" />
                            <Typography variant="caption" color="text.secondary">
                              {stats ? `${stats.total} applications` : 'Applications'}
                            </Typography>
                            {isLoadingApplications && <CircularProgress size={16} />}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Stack spacing={2}>
                      {job.description ? (
                        <Typography variant="body2" color="text.secondary">
                          {job.description}
                        </Typography>
                      ) : null}

                      <Divider />

                      <Typography variant="subtitle2" fontWeight="medium">
                        Applications
                      </Typography>

                      {isLoadingApplications ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={18} />
                          <Typography variant="body2" color="text.secondary">
                            Loading applications…
                          </Typography>
                        </Box>
                      ) : applications ? (
                        applications.length > 0 ? (
                          <Stack spacing={1.5}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                              {stats?.pending ? <Chip label={`${stats.pending} Pending`} color="warning" variant="outlined" size="small" /> : null}
                              {stats?.reviewed ? <Chip label={`${stats.reviewed} Reviewed`} color="info" variant="outlined" size="small" /> : null}
                              {stats?.accepted ? <Chip label={`${stats.accepted} Accepted`} color="success" variant="outlined" size="small" /> : null}
                              {stats?.rejected ? <Chip label={`${stats.rejected} Rejected`} color="error" variant="outlined" size="small" /> : null}
                              <Chip
                                icon={<FilterListIcon />}
                                label="Shortlisted"
                                size="small"
                                color={shortlistFilterByJobId[job.id] ? 'primary' : 'info'}
                                variant={shortlistFilterByJobId[job.id] ? 'filled' : 'outlined'}
                                onClick={() => handleToggleShortlistFilter(job.id)}
                                sx={{ ml: 'auto' }}
                              />
                            </Box>

                            {(shortlistFilterByJobId[job.id]
                              ? applications.filter(a => (shortlistedByJobId[job.id] || []).includes(a.jobSeekerProfileId))
                              : applications
                            ).map((application) => {
                              const whatsAppPhone = cleanPhoneForWhatsApp(
                                application.basicProfile?.whatsapp || application.basicProfile?.phone
                              );
                              return (
                                <Box
                                  key={application.id}
                                  sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    bgcolor: 'background.paper'
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                      {application.basicProfile?.firstName
                                        ? application.basicProfile.firstName.charAt(0).toUpperCase()
                                        : <PersonIcon fontSize="small" />}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Tooltip title={(shortlistedByJobId[job.id] || []).includes(application.jobSeekerProfileId) ? 'Remove from shortlist' : 'Add to shortlist'}>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleToggleShortlist(job.id, application.jobSeekerProfileId)}
                                            disabled={togglingShortlist.has(`${job.id}:${application.jobSeekerProfileId}`)}
                                            sx={{ p: 0.25 }}
                                          >
                                            {togglingShortlist.has(`${job.id}:${application.jobSeekerProfileId}`) ? (
                                              <CircularProgress size={16} />
                                            ) : (shortlistedByJobId[job.id] || []).includes(application.jobSeekerProfileId) ? (
                                              <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                                            ) : (
                                              <StarBorderIcon fontSize="small" color="action" />
                                            )}
                                          </IconButton>
                                        </Tooltip>
                                        <Typography variant="body2" fontWeight="medium">
                                          {application.basicProfile?.firstName
                                            ? `${application.basicProfile.firstName}${application.basicProfile.lastName ? ' ' + application.basicProfile.lastName : ''}`
                                            : 'Applicant'}
                                        </Typography>
                                        <Chip
                                          label={application.status || 'pending'}
                                          size="small"
                                          color={
                                            application.status === 'accepted' ? 'success' :
                                            application.status === 'rejected' ? 'error' :
                                            application.status === 'reviewed' ? 'info' : 'warning'
                                          }
                                          variant="outlined"
                                          sx={{ textTransform: 'capitalize' }}
                                        />
                                        <Button
                                          size="small"
                                          variant="text"
                                          href={`/jobseeker/public/${application.jobSeekerProfileId}${job.id ? `?jobId=${job.id}` : ''}`}
                                          target="_blank"
                                          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                                          sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, p: '2px 6px' }}
                                        >
                                          View Profile
                                        </Button>
                                        {whatsAppPhone && (
                                          <Tooltip title="Contact via WhatsApp">
                                            <IconButton
                                              size="small"
                                              component="a"
                                              href={`https://wa.me/${whatsAppPhone}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              sx={{ p: 0.25, color: '#25D366' }}
                                            >
                                              <WhatsAppIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<RateReviewIcon fontSize="small" />}
                                          onClick={() => setFeedbackTarget({
                                            jobSeekerProfileId: application.jobSeekerProfileId,
                                            name: application.basicProfile?.firstName
                                              ? `${application.basicProfile.firstName}${application.basicProfile.lastName ? ' ' + application.basicProfile.lastName : ''}`
                                              : 'this worker',
                                            jobId: job.id,
                                            applicationId: application.id,
                                          })}
                                          sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1 }}
                                        >
                                          Leave Feedback
                                        </Button>
                                      </Box>
                                      <Typography variant="caption" color="text.secondary">
                                        Applied {formatDate(application.appliedAt)}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {application.applicationMessage ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                      &ldquo;{application.applicationMessage}&rdquo;
                                    </Typography>
                                  ) : null}

                                  {application.jobSeekerProfile ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                      Experience: {application.jobSeekerProfile.experience || 'Not specified'} • Location: {application.jobSeekerProfile.location || 'Not specified'}
                                    </Typography>
                                  ) : null}
                                </Box>
                              );
                            })}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No applications yet
                          </Typography>
                        )
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Expand to load applications
                        </Typography>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {/* Load More */}
            {nextCursor && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                >
                  {loadingMore ? 'Loading…' : 'Load More Jobs'}
                </Button>
              </Box>
            )}
          </Stack>
        )}
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <FeedbackDialog
        open={!!feedbackTarget}
        onClose={() => setFeedbackTarget(null)}
        ratedProfileType="jobseeker"
        ratedProfileId={feedbackTarget?.jobSeekerProfileId}
        ratedName={feedbackTarget?.name}
        raterProfileType="company"
        raterProfileId={companyProfile?.id}
        raterName={companyProfile?.companyName || ''}
        jobId={feedbackTarget?.jobId}
        applicationId={feedbackTarget?.applicationId}
      />
    </Container>
  );
};

export default CompanyJobsManagement;
